import { useEffect } from 'react';
import { ActivityIndicator, View } from 'react-native';

import { logError } from '@/modules/analytics';

import { closeWebOnboardingInNative } from './utils/closeWebOnboardingInNative';
import { handoffSesameToNative } from './utils/handoffSesameToNative';

/**
 * Delay between handing the sesame to native and dismissing the WebView.
 *
 * Native exchanges the sesame for a fresh session asynchronously with no ack
 * channel, so give it a head start before we close (Sparta's exit broker used
 * the same delay). For an anonymous "Try Vida Now" user there is no prior
 * native session to fall back on, so losing this race would drop them
 * unauthenticated — hence we wait rather than close immediately.
 */
const SESAME_HANDOFF_CLOSE_DELAY_MS = 1500;

/**
 * NATIVE HANDOFF — the onboarding terminal inside the native WebView (Sparta's
 * onboardingCompleteEpic native path — the native completion REPLACES the web
 * redirect). On mount it hands the session back to the host app and dismisses
 * the WebView, so the user never sees the web transition screen (QR + app
 * download). Renders a neutral loader for the moment the dismiss takes.
 *
 * The handoff refreshes native's session in-app: it mints a sesame from our
 * own Bearer and hands it to native via `handleAuth`, which exchanges it for a
 * fresh JWT. Cookie-free, so it works on iOS and Android alike (unlike
 * bouncing to Sparta's www.vida.com broker, whose cross-origin session cookie
 * Android does not persist). A no-op on any mint/bridge failure.
 */
export function NativeHandoffScreen() {
  useEffect(() => {
    void (async () => {
      if (await handoffSesameToNative()) {
        // Give native a head start to exchange the sesame before we dismiss.
        await new Promise((resolve) => setTimeout(resolve, SESAME_HANDOFF_CLOSE_DELAY_MS));
      }
      // Dismiss the WebView. If the embed was declared (vida_client=ios/android)
      // but no host bridge was injected — e.g. native-shell version-skew — the
      // dismiss silently fails and the user is stranded here. Log it (Sparta's
      // initiateCloseOnboardingInNative logs the same fork) so the hang is
      // triagable.
      if (!closeWebOnboardingInNative()) {
        void logError('Failed to close web onboarding in native app: no host bridge present');
      }
    })();
  }, []);

  return (
    <View
      className='bg-background-0'
      style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
    >
      <ActivityIndicator />
    </View>
  );
}
